import { useState, useMemo } from 'react'
import { Select, useModal, Button } from '@panwds/react-ui'
import { APP_ID_DSS_ID, APP_ID_PRISMA_ACCESS, APP_ID_PRISMA_ACCESS_EDITION } from '../../../../common/constants/GlobalConstants'
import { FF_ENABLE_CIE_SHARING, FF_ENABLE_INSTANCE_LICENSE_EDIT } from '../../../../common/utils/FeatureFlags'
import { getVarValue } from '../../../../common/utils/Sparky'
import DeleteInstanceModal from '../DeleteInstanceModal'
import CIESharingModal from '../Modal/CIESharingModal'
import LoadingModal from '../Modal/LoadingModal'
import RenameInstanceModal from '../Modal/RenameInstanceModal'
import { history } from '@sparky/framework'
import SubscriptionService from '../../../../activation/services/SubscriptionService'
import { useMessage } from '../../../../common/providers/MessageProvider'
import { MoreIcon } from '@panwds/icons'
import { useProductInfoModule } from '../Modal/ProductInfoModal'

const PRODUCT_INFO_ACTION = 'PRODUCT_INFO_ACTION'
const CIE_SHARING_ACTION = 'CIE_SHARING_ACTION'
const DELETE_ACTION = 'DELETE_ACTION'
const EDIT_ACTION = 'EDIT_ACTION'

const PRODUCT_INFO_DISPLAY_NAME = 'Product Information'
const MANAGE_CIE_SHARING = 'Manage Sharing'
const EDIT_PA_LICENSE = 'Edit License'
const RENAME_INSTANCE_DISPLAY_NAME = 'Rename Instance'
const RENAME_ACTION = 'RENAME_ACTION'
const DELETE_ACTION_DISPLAY = <span className='tw-text-red-500'>Delete</span>

const isFreeApp = (app) => (!app ? false : !app.auth_code && !app.use_nova && !app.use_pubsub && !app?.flags?.use_license_hash_only)

const isThirdParty = (app) => {
  return Boolean(app?.is_third_party)
}

const getActions = (app, instance) => {
  const enableCieSharing = getVarValue(FF_ENABLE_CIE_SHARING, false)
  const enableEdit = getVarValue(FF_ENABLE_INSTANCE_LICENSE_EDIT, false)
  const actions = []
  if (enableCieSharing && app?.app_id === APP_ID_DSS_ID && !instance?.original_tsg_id) {
    actions.push({ value: MANAGE_CIE_SHARING, id: CIE_SHARING_ACTION })
  }
  actions.push({ value: PRODUCT_INFO_DISPLAY_NAME, id: PRODUCT_INFO_ACTION })
  app?.flags?.is_rename_allowed && actions.push({ value: RENAME_INSTANCE_DISPLAY_NAME, id: RENAME_ACTION })
  if (isThirdParty(app)) {
    // if (app.developer_defined_fields?.length) {
    //   actions.push({ value: EDIT_ACTION_DISPLAT_NAME, id: EDIT_ACTION })
    // }
    if (app.deletable !== false) {
      actions.push({ value: DELETE_ACTION_DISPLAY, id: DELETE_ACTION })
    }
  }
  else if (isFreeApp(app) && app.deletable && !(enableCieSharing && app?.app_id === APP_ID_DSS_ID && instance?.original_tsg_id)) {
    actions.push({ value: DELETE_ACTION_DISPLAY, id: DELETE_ACTION })
  }
  if (enableEdit && app?.app_id === APP_ID_PRISMA_ACCESS) {
    let disabled = true
    if (!instance.tenant_id.includes('_onprem') && (instance.status === 'running' || (!instance.status && instance.provisioning_status === 'complete'))) {
      // only fawkes managed (tenant_id doesn't include _onprem) and status is ok, we enabled edit license
      disabled = false
    }
    actions.push({ value: EDIT_PA_LICENSE, id: EDIT_ACTION, disabled })
  }

  return actions
}

const ActionRenderer = ({ row, onDeleteInstance, onRenameInstance, selectedTreeNode, flattenTree, ...rest }) => {
  const { createBanner } = useMessage()
  const [selectedItem, setSelectedItem] = useState()
  const app = row?.original?.app
  const instance = row?.original
  const xsig = row?.original?.xsig
  const { openModal: openInfoModal } = useProductInfoModule(instance)
  const { openModal: openDeleteModal } = useModal(DeleteInstanceModal)
  const { openModal: openRenameModal } = useModal(RenameInstanceModal)
  const { openModal: openCieSharingModal } = useModal(CIESharingModal)
  const { openModal: openLoadingModal, closeModal: closeLoadingModal } = useModal(LoadingModal)

  const onActionClick = async ({ selectedItem }) => {
    setSelectedItem(selectedItem)
    switch (selectedItem.id) {
      case PRODUCT_INFO_ACTION:
        openInfoModal()
        setSelectedItem(null)
        break
      case DELETE_ACTION:
        openDeleteModal({ instance, app, xsig, onDeleteInstance })
        setSelectedItem(null)
        break
      case RENAME_ACTION:
        openRenameModal({ instance, app, onRenameInstance })
        setSelectedItem(null)
        break
      case CIE_SHARING_ACTION:
        openCieSharingModal({ instance, app, selectedTreeNode, openCieSharingModal, flattenTree })
        setSelectedItem(null)
        break
      case EDIT_ACTION: {
        // find license_id and jump to license page
        const instanceTsg = instance.tsg_id
        openLoadingModal()
        const { data } = await SubscriptionService.licenses.get({
          support_account_ids: [instance.support_account_id],
          tsg_id: instanceTsg,
          app_id: `${APP_ID_PRISMA_ACCESS_EDITION}`,
        }).catch(() => {
          createBanner({ message: 'error finding licenses with this instance', bannerProps: { showIcon: true, appearance: 'error' } })
        })
        closeLoadingModal()
        const currentLicense = data.find(each => each.calculated_filters?.associated_tenants?.hasOwnProperty(instanceTsg))
        if (currentLicense?.license_id) {
          // check if there's remaining
          if (currentLicense.calculated_filters?.is_license_remaining) {
            history.push(`/activation/licenses/prisma_access/${currentLicense.license_id}?locked_tsg_ids=${instance.tsg_id}`)
          }
          else {
            createBanner({ message: 'There\'s no remaining license to edit', bannerProps: { showIcon: true, appearance: 'warning' } })
          }
        }
        else {
          createBanner({ message: 'cannot find the license_id related with this instance', bannerProps: { showIcon: true, appearance: 'error' } })
        }
        setSelectedItem(null)
        break
      }
      default:
      // do nothing
    }
  }

  const items = useMemo(() => getActions(app, instance, row), [app, instance, row])

  return (
    app ? <Select
      addClassName='cmm-modal-select'
      items={items}
      onChange={onActionClick}
      selectedItem={selectedItem}
      fitToItemWidth
      button={
        <Button
          aria-label='actions'
          appearance='tertiary-clear'
          icon={<MoreIcon size='sm'/>}
        />
      }
      defaultPlacement='left-end'
    /> : <div />
  )
}

export default ActionRenderer
